import { DeviceSymbol, FieldAreaSymbol } from './legend-symbols';
import styles from './map-legend.module.scss';

export type MapLegendProps = {
  /** The Grower has at least one Boundary actually DRAWN on the map. */
  hasFieldAreas: boolean;
  /** The Grower has at least one Device actually DRAWN on the map — one with a plottable location. */
  hasDevices: boolean;
};

/**
 * The map's key, top right (Figma `787:681184`). **Presence-driven**: a swatch appears only for an
 * element type the Grower actually has, and a Grower with neither gets no legend at all rather than
 * an empty container over the canvas (113584 §10a–§10c, §10e, §13a). Every Figma frame shows both
 * swatches because both-present is the only case anyone mocks.
 *
 * Both props must be read from the layers **as drawn**, never from the payload as received — see
 * `<MapPage>`, which computes them through the same two functions the canvas draws from. That
 * collapses all three ways a layer can contribute nothing into one answer, no swatch: the layer is
 * empty, everything in it is undrawable, or the layer failed to load. A legend that promised a
 * symbol nothing on the canvas can match, next to a notice saying the layer is missing, would be
 * worse than no legend.
 *
 * Nothing here is interactive, which is how §10d ("clicking a legend item causes no filtering,
 * hiding or navigation") is guaranteed — there is no click target to wire wrongly later.
 */
export function MapLegend({ hasFieldAreas, hasDevices }: MapLegendProps) {
  if (!hasFieldAreas && !hasDevices) return null;

  return (
    <div className={styles.legend} role="group" aria-label="Map legend">
      {hasFieldAreas ? (
        <p className={styles.item}>
          <FieldAreaSymbol />
          Field Area
        </p>
      ) : null}
      {hasDevices ? (
        <p className={styles.item}>
          <DeviceSymbol />
          Device
        </p>
      ) : null}
    </div>
  );
}
